import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import { CalendarDays, FileText, MessageSquare, GraduationCap, Send, Download, Plus } from "lucide-react";

const LETTERS = ["A+", "A", "A-", "B+", "B", "B-", "C+", "C", "C-", "D+", "D", "D-", "F"];
const TABS = ["schedule", "grades", "reports", "messages"];
const TAB_ICON = { schedule: CalendarDays, grades: GraduationCap, reports: FileText, messages: MessageSquare };

/**
 * Shared SIS panel used by both Parent (read-only on grades/reports) and Admin (full edit).
 * Props: studentId (required), editable (bool, default false = parent view).
 */
export function StudentSISPanel({ studentId, editable = false }) {
  const { lang } = useLang();
  const t = (fr, en) => (lang === "fr" ? fr : en);
  const [searchParams] = useSearchParams();
  const initialTab = TABS.includes(searchParams.get("tab")) ? searchParams.get("tab") : "schedule";
  const [tab, setTab] = useState(initialTab);
  const [data, setData] = useState(null);
  const [messages, setMessages] = useState([]);
  const [familyId, setFamilyId] = useState(null);
  const [newMsg, setNewMsg] = useState("");
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data: sis } = await api.get(`/students/${studentId}/sis`);
      setData(sis);
      setFamilyId(sis.family?.id);
      // Load messages
      if (editable && sis.family?.id) {
        const { data: msgs } = await api.get(`/families/${sis.family.id}/messages`);
        setMessages(msgs);
      } else {
        const { data: mine } = await api.get(`/messages/mine`);
        setMessages(mine.messages || []);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (studentId) load(); /* eslint-disable-next-line */ }, [studentId, editable]);

  if (loading || !data) return <div className="p-8 text-muted-foreground">{t("Chargement…", "Loading…")}</div>;

  const { student, family, courses, terms, grades, report_cards } = data;

  return (
    <div data-testid="sis-panel">
      {/* Header */}
      <Card className="mb-6">
        <CardContent className="p-5 flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="overline mb-1">{t("Espace élève", "Student space")}</p>
            <h2 className="font-display font-bold text-3xl tracking-tighter">
              {student.first_name} {student.last_name}
            </h2>
            <div className="flex flex-wrap gap-2 mt-2 text-xs text-muted-foreground">
              <Badge variant="outline">{student.grade || "—"}</Badge>
              <span>· {family?.parent_first_name} {family?.parent_last_name}</span>
              <span>· {family?.email}</span>
            </div>
          </div>
          {!editable && (
            <Badge className="bg-accent text-accent-foreground">{t("Inscrit", "Enrolled")}</Badge>
          )}
        </CardContent>
      </Card>

      {/* Tabs */}
      <div className="flex flex-wrap gap-1 mb-4 border-b border-border" data-testid="sis-tabs">
        {TABS.map((k) => {
          const Icon = TAB_ICON[k];
          const labels = {
            schedule: [t("Emploi du temps", "Schedule"), CalendarDays],
            grades: [t("Notes", "Grades"), GraduationCap],
            reports: [t("Bulletins", "Report cards"), FileText],
            messages: [t("Messagerie", "Messages"), MessageSquare],
          };
          const active = tab === k;
          return (
            <button
              key={k}
              data-testid={`sis-tab-${k}`}
              onClick={() => setTab(k)}
              className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 transition-colors ${active ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"}`}
            >
              <Icon className="w-4 h-4" /> {labels[k][0]}
            </button>
          );
        })}
      </div>

      {tab === "schedule" && <ScheduleTab courses={courses} t={t} studentId={studentId} editable={editable} reload={load} />}
      {tab === "grades" && <GradesTab studentId={studentId} courses={courses} terms={terms} grades={grades} editable={editable} reload={load} t={t} lang={lang} />}
      {tab === "reports" && <ReportsTab studentId={studentId} terms={terms} reports={report_cards} editable={editable} reload={load} t={t} />}
      {tab === "messages" && <MessagesTab messages={messages} editable={editable} familyId={familyId} newMsg={newMsg} setNewMsg={setNewMsg} reload={load} t={t} />}
    </div>
  );
}

function ScheduleTab({ courses, t, studentId, editable, reload }) {
  const required = courses.filter(c => c.subject === "english");
  const optional = courses.filter(c => c.subject === "us_history");
  const other = courses.filter(c => !["english", "us_history"].includes(c.subject));
  const [diploma, setDiploma] = useState(null);

  useEffect(() => {
    api.get(`/students/${studentId}/diploma`).then(r => setDiploma(r.data)).catch(() => {});
  }, [studentId]);

  const enrollUSH = async () => {
    try {
      const { data } = await api.post(
        `/students/${studentId}/us-history/deposit/checkout`,
        { student_id: studentId, origin_url: window.location.origin },
      );
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error("Stripe — aucune URL reçue");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  const renderCourseCard = (c) => (
    <div key={c.id} className="border border-border rounded-lg p-4 bg-card" data-testid={`schedule-${c.id}`}>
      <div className="font-display text-lg">{c.name_fr}</div>
      <div className="text-xs text-muted-foreground mt-1">{c.name_en}</div>
      <div className="text-xs mt-2 flex gap-2 flex-wrap items-center">
        <Badge variant="outline">{c.grade_level}</Badge>
        <span>{c.day} · {c.time}</span>
        <span className="text-muted-foreground">({c.timezone})</span>
      </div>
      <div className="text-xs text-muted-foreground mt-2">{c.teacher}</div>
    </div>
  );

  return (
    <div className="space-y-5">
      {/* Required English */}
      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl flex items-center gap-2">
            {t("Cours d'anglais", "English course")}
            <Badge className="bg-primary text-primary-foreground text-[10px]">{t("Obligatoire", "Required")}</Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {required.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("Le cours d'anglais sera assigné automatiquement après l'inscription.", "The English course is auto-assigned after enrollment.")}</p>
          ) : (
            <div className="grid sm:grid-cols-2 gap-3" data-testid="schedule-required">{required.map(renderCourseCard)}</div>
          )}
        </CardContent>
      </Card>

      {/* US History — only relevant from 3e */}
      {diploma?.eligible_to_enroll && (
        <Card data-testid="us-history-card" className="border-accent/40">
          <CardHeader>
            <CardTitle className="font-display text-xl flex items-center gap-2">
              US History
              <Badge variant="outline" className="text-[10px]">{t("Optionnel", "Optional")}</Badge>
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-1">{t(
              `Cours optionnel ouvert dès la 3ème. ${diploma.years_required} années sont requises pour valider le Bilingual High School Diploma.`,
              `Optional course available from 9th grade. ${diploma.years_required} years required to validate the Bilingual High School Diploma.`,
            )}</p>
          </CardHeader>
          <CardContent className="space-y-3">
            {optional.length > 0 ? (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="schedule-us-history">{optional.map(renderCourseCard)}</div>
            ) : (
              <div className="text-sm text-muted-foreground bg-secondary/40 rounded-lg p-4">
                {t("Pas encore inscrit en US History pour cette année.", "Not yet enrolled in US History for this year.")}
              </div>
            )}
            {optional.length === 0 && (
              <Button data-testid="enroll-us-history-btn" onClick={enrollUSH} className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-full">
                + {t("Ajouter US History (acompte 95 €)", "Add US History (€95 deposit)")}
              </Button>
            )}
            {/* Diploma progress */}
            <div className="border-t border-border pt-3">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {t("Bilingual High School Diploma", "Bilingual High School Diploma")}
                </div>
                <div className="text-xs tabular-nums text-muted-foreground" data-testid="diploma-progress">
                  {diploma.years_validated} / {diploma.years_required} {t("années validées", "years validated")}
                </div>
              </div>
              <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent transition-all"
                  style={{ width: `${Math.min(100, (diploma.years_validated / diploma.years_required) * 100)}%` }}
                />
              </div>
              {diploma.eligible && (
                <div className="text-xs text-accent font-semibold mt-2" data-testid="diploma-eligible">
                  {t("✓ Toutes les années validées — diplôme éligible", "✓ All years validated — diploma eligible")}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Other courses if any */}
      {other.length > 0 && (
        <Card>
          <CardHeader><CardTitle className="font-display text-xl">{t("Autres cours", "Other courses")}</CardTitle></CardHeader>
          <CardContent><div className="grid sm:grid-cols-2 gap-3">{other.map(renderCourseCard)}</div></CardContent>
        </Card>
      )}
    </div>
  );
}

function GradesTab({ studentId, courses, terms, grades, editable, reload, t, lang }) {
  const sortedTerms = [...terms].sort((a, b) => (a.start_date || "").localeCompare(b.start_date || ""));
  const [activeTerm, setActiveTerm] = useState(terms.find(x => x.is_current)?.id || sortedTerms[0]?.id);
  const gradeFor = (courseId) => grades.find(g => g.course_id === courseId && g.term_id === activeTerm);
  const term = terms.find(x => x.id === activeTerm);

  const save = async (courseId, letter, comment) => {
    try {
      await api.post(`/students/${studentId}/grades`, { course_id: courseId, term_id: activeTerm, letter, comment });
      toast.success(t("Note enregistrée", "Grade saved"));
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center" data-testid="term-picker">
        {sortedTerms.map(tm => (
          <button
            key={tm.id}
            data-testid={`term-${tm.code}`}
            onClick={() => setActiveTerm(tm.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${activeTerm === tm.id ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:text-foreground"}`}
          >
            {lang === "fr" ? tm.label_fr : tm.label_en}
            {tm.type === "progress_report" && <span className="ml-1 opacity-70">·</span>}
          </button>
        ))}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="font-display text-xl">
            {lang === "fr" ? term?.label_fr : term?.label_en}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {courses.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("Aucun cours.", "No courses.")}</p>
          ) : (
            <div className="space-y-2" data-testid="grades-list">
              {courses.map(c => {
                const g = gradeFor(c.id);
                return (
                  <GradeRow key={c.id} course={c} grade={g} editable={editable} onSave={save} t={t} lang={lang} />
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function GradeRow({ course, grade, editable, onSave, t, lang }) {
  const [letter, setLetter] = useState(grade?.letter || "");
  const [comment, setComment] = useState(grade?.comment || "");
  const [editing, setEditing] = useState(false);
  useEffect(() => { setLetter(grade?.letter || ""); setComment(grade?.comment || ""); }, [grade]);

  if (!editable) {
    return (
      <div className="border border-border rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`grade-row-${course.id}`}>
        <div>
          <div className="text-sm font-medium">{lang === "fr" ? course.name_fr : course.name_en}</div>
          <div className="text-xs text-muted-foreground">{course.teacher}</div>
          {grade?.comment && <div className="text-xs italic text-muted-foreground mt-1">"{grade.comment}"</div>}
        </div>
        <div className="text-2xl font-bold text-accent tabular-nums" data-testid={`grade-letter-${course.id}`}>
          {grade?.letter || "—"}
        </div>
      </div>
    );
  }

  return (
    <div className="border border-border rounded-lg p-3" data-testid={`grade-row-${course.id}`}>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-medium">{lang === "fr" ? course.name_fr : course.name_en}</div>
          <div className="text-xs text-muted-foreground">{course.teacher}</div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={letter} onValueChange={setLetter}>
            <SelectTrigger className="w-24" data-testid={`grade-select-${course.id}`}><SelectValue placeholder="—" /></SelectTrigger>
            <SelectContent>{LETTERS.map(l => <SelectItem key={l} value={l}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Button size="sm" variant="outline" onClick={() => setEditing(!editing)}>{editing ? t("Fermer", "Close") : t("Commentaire", "Comment")}</Button>
          <Button size="sm" data-testid={`grade-save-${course.id}`} disabled={!letter || letter === grade?.letter && comment === (grade?.comment || "")} onClick={() => onSave(course.id, letter, comment)}>{t("Enregistrer", "Save")}</Button>
        </div>
      </div>
      {editing && (
        <Textarea
          className="mt-2"
          rows={2}
          placeholder={t("Appréciation de l'enseignant…", "Teacher comment…")}
          value={comment}
          onChange={e => setComment(e.target.value)}
          data-testid={`grade-comment-${course.id}`}
        />
      )}
    </div>
  );
}

function ReportsTab({ studentId, terms, reports, editable, reload, t }) {
  const [genTerm, setGenTerm] = useState(terms[0]?.id || "");
  const [headComment, setHeadComment] = useState("");
  const [generating, setGenerating] = useState(false);
  const [diploma, setDiploma] = useState(null);
  const { lang } = useLang();

  useEffect(() => {
    api.get(`/students/${studentId}/diploma`).then(r => setDiploma(r.data)).catch(() => {});
  }, [studentId]);

  const generate = async () => {
    if (!genTerm) return;
    setGenerating(true);
    try {
      await api.post(`/students/${studentId}/report-cards`, { term_id: genTerm, head_comment: headComment });
      toast.success(t("Bulletin généré", "Report card generated"));
      setHeadComment("");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setGenerating(false); }
  };

  const download = async (rid, filename) => {
    try {
      const res = await api.get(`/report-cards/${rid}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = filename || "bulletin.pdf";
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <div className="space-y-4">
      {diploma?.eligible_to_enroll && (
        <Card data-testid="diploma-banner" className="border-accent/40 bg-accent/5">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-primary">
                  {t("Bilingual High School Diploma", "Bilingual High School Diploma")}
                </div>
                <div className="text-xs text-muted-foreground">
                  {t("2 années de US History (note S2 ≥ D) sont requises avant la Terminale.",
                     "2 years of US History (S2 grade ≥ D) required before 12th grade.")}
                </div>
              </div>
              <div className="text-sm font-bold tabular-nums text-accent">
                {diploma.years_validated} / {diploma.years_required}
              </div>
            </div>
            <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
              <div className="h-full bg-accent transition-all"
                style={{ width: `${Math.min(100, (diploma.years_validated / diploma.years_required) * 100)}%` }} />
            </div>
            {diploma.eligible && (
              <div className="text-xs font-semibold text-accent mt-2">
                {t("✓ Toutes les années validées — diplôme éligible", "✓ All years validated — diploma eligible")}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {editable && (
        <Card data-testid="generate-report-card">
          <CardHeader><CardTitle className="font-display text-xl">{t("Générer un bulletin / bilan", "Generate a report card / progress report")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <Select value={genTerm} onValueChange={setGenTerm}>
              <SelectTrigger data-testid="gen-term-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {terms.map(tm => (
                  <SelectItem key={tm.id} value={tm.id}>
                    {lang === "fr" ? tm.label_fr : tm.label_en}
                    {tm.type === "progress_report" ? " · Bilan" : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Textarea
              rows={3}
              placeholder={t("Appréciation générale du conseil de classe…", "Head of school's overall comment…")}
              value={headComment}
              onChange={e => setHeadComment(e.target.value)}
              data-testid="gen-head-comment"
            />
            <Button onClick={generate} disabled={generating || !genTerm} className="bg-primary text-primary-foreground hover:bg-primary/90 rounded-full" data-testid="gen-report-btn">
              <Plus className="w-4 h-4 mr-1" /> {generating ? "…" : t("Générer le PDF", "Generate PDF")}
            </Button>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader><CardTitle className="font-display text-xl">{t("Bulletins disponibles", "Available reports")}</CardTitle></CardHeader>
        <CardContent>
          {reports.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t("Aucun bulletin pour l'instant.", "No reports yet.")}</p>
          ) : (
            <ul className="space-y-2" data-testid="reports-list">
              {reports.map(r => {
                const term = terms.find(x => x.id === r.term_id);
                return (
                  <li key={r.id} className="border border-border rounded-lg p-3 flex items-center justify-between gap-3" data-testid={`report-${r.id}`}>
                    <div>
                      <div className="text-sm font-medium">{term ? (lang === "fr" ? term.label_fr : term.label_en) : r.type}</div>
                      <div className="text-xs text-muted-foreground">{new Date(r.generated_at).toLocaleDateString()} · {r.generated_by}</div>
                    </div>
                    <Button size="sm" variant="outline" data-testid={`dl-report-${r.id}`} onClick={() => download(r.id, r.filename)}>
                      <Download className="w-3 h-3 mr-1" /> PDF
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function MessagesTab({ messages, editable, familyId, newMsg, setNewMsg, reload, t }) {
  const endRef = useRef(null);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages.length]);

  const send = async () => {
    if (!newMsg.trim()) return;
    try {
      const payload = { body: newMsg };
      if (editable) payload.family_id = familyId;
      await api.post(`/messages`, payload);
      setNewMsg("");
      reload();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-display text-xl">{t("Messagerie avec l'école", "Messaging with the school")}</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="border border-border rounded-lg bg-secondary/30 p-3 h-80 overflow-y-auto space-y-2" data-testid="messages-list">
          {messages.length === 0 ? (
            <div className="text-center text-sm text-muted-foreground py-8">{t("Aucun message pour l'instant. Lancez la conversation !", "No messages yet — start the conversation!")}</div>
          ) : messages.map(m => {
            const mine = (editable && m.sender_role === "admin") || (!editable && m.sender_role === "parent");
            return (
              <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.id}`}>
                <div className={`max-w-[75%] rounded-2xl px-3 py-2 text-sm ${mine ? "bg-primary text-primary-foreground" : "bg-card border border-border"}`}>
                  <div className="text-[10px] opacity-70 mb-0.5 uppercase tracking-wider">{m.sender_name || m.sender_email}</div>
                  <div className="whitespace-pre-line">{m.body}</div>
                  <div className="text-[10px] opacity-60 mt-1">{new Date(m.created_at).toLocaleString()}</div>
                </div>
              </div>
            );
          })}
          <div ref={endRef} />
        </div>
        <div className="flex gap-2 mt-3">
          <Textarea
            rows={2}
            value={newMsg}
            onChange={(e) => setNewMsg(e.target.value)}
            placeholder={t("Écrire un message…", "Write a message…")}
            data-testid="new-message"
            className="flex-1"
            onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) send(); }}
          />
          <Button onClick={send} disabled={!newMsg.trim()} className="bg-primary text-primary-foreground hover:bg-primary/90 self-end" data-testid="send-message-btn">
            <Send className="w-4 h-4 mr-1" /> {t("Envoyer", "Send")}
          </Button>
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">{t("Astuce : Ctrl/Cmd + Entrée pour envoyer", "Tip: Ctrl/Cmd + Enter to send")}</p>
      </CardContent>
    </Card>
  );
}
